import React, { createContext, useContext, useEffect, useState } from 'react'
import {
  SmarticoLoginError,
  SmarticoLogoutError,
  SmarticoStartupError,
  SmarticoUpdatePushNotificationTokenError,
} from '../errors/smartico'
import {
  init as initSmartico,
  login as smarticoLogin,
  logout as smarticoLogout,
  updatePushNotificationToken,
  waitForSmartico,
} from '../utils/smarticoUtils'
import { Logger } from '../utils/loggerUtils'
import { AuthContext } from './authProvider'
import { tryCatch } from '../helpers/tryCatch'

const logger = new Logger.Builder()
  .setContext('Smartico')
  .enableSentryIntegration()
  .build()

const defaultValues = {
  isLoading: false,
  isLoggedIn: false,
  login: () => {},
  logout: () => {},
  onError: () => {},
  onLoad: () => {},
}

const SmarticoContext = createContext(defaultValues)

export const SmarticoProvider = ({ children }) => {
  const authContext = useContext(AuthContext)

  const [isInitialized, setIsInitialized] = useState(false)
  const [isLoggedIn, setIsLoggedIn] = useState(false)

  const start = async () => {
    const result = await tryCatch(() => waitForSmartico())
    if (result.error) {
      logger.error(new SmarticoStartupError(result.error))
      return
    }

    setupSmartico()
  }
  
  const setupSmartico = () => {
    const result = tryCatch(() => initSmartico())
    if (result.error) {
      logger.error(new SmarticoStartupError(result.error))
      return
    }

    setIsInitialized(true)
  }

  const login = async (user) => {
    const result = await tryCatch(() => smarticoLogin(user))
    if (result.error) {
      logger.error(new SmarticoLoginError(result.error))
      return
    }

    setIsLoggedIn(true)
  }

  const logout = () => {
    const { error } = tryCatch(() => smarticoLogout())
    if (error) {
      logger.error(new SmarticoLogoutError(error))
      return
    }

    setIsLoggedIn(false)
  }

  const updateToken = () => {
    const { error } = tryCatch(() => updatePushNotificationToken())
    if (error) {
      logger.error(new SmarticoUpdatePushNotificationTokenError(error))
    }
  }

  useEffect(() => {
    start()
  }, [])

  useEffect(() => {
    window.addEventListener('PUSH_NOTIFICATIONS_TOKEN', updateToken)
    
    return () => {
      window.removeEventListener('PUSH_NOTIFICATIONS_TOKEN', updateToken)
    }
  }, [])

  useEffect(() => {
    if (!isInitialized) return

    const shouldLogin = authContext.isLoggedIn && !isLoggedIn
    const shouldLogout = !authContext.isLoggedIn && isLoggedIn

    if (shouldLogin) {
      login(authContext.user)
      return
    }
    
    if (shouldLogout) {
      logout()
      return
    }
  }, [authContext.isLoggedIn, authContext.user, isInitialized, isLoggedIn])

  const value = { isLoggedIn }

  return (
    <SmarticoContext.Provider value={value}>
      {children}
    </SmarticoContext.Provider>
  )
}

export const useSmartico = () => useContext(SmarticoContext)